import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

/** Gives the toast time to actually be read before the redirect fires. */
const SUCCESS_REDIRECT_DELAY_MS = 1200;

@Component({
  selector: 'app-complete-profile',
  standalone: true,
  imports: [FormsModule, CommonModule, ToastModule],
  providers: [MessageService],
  templateUrl: './complete-profile.component.html',
  styleUrl: './complete-profile.component.scss',
})
export class CompleteProfileComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);

  firstName = '';
  lastName = '';
  password = '';
  /** Optional -- another user's referral code (e.g. "JOHN4821"). When valid,
   *  both this new signup and the referrer get 1000 coins. */
  referredByCode = '';
  loading = false;
  errorMessage = '';
  showPassword = false;

  /** True when the auth account already exists (e.g. Google sign-in or an
   *  email/password user with no profile yet). We must only create the UserProfile
   *  row, not call /auth/register again (which would create a duplicate account). */
  readonly isSocialSignup = localStorage.getItem('ip-auth-provider') === 'social';

  /** True for the "Corporate" signup path, these users must also provide
   *  company details before they can continue (see `step`). */
  readonly isCorporateSignup = localStorage.getItem('ip-auth-role') === 'corporates';

  /** 'profile' = name/password form; 'company' = mandatory company-details
   *  form shown only to corporate signups, after their account is created. */
  step: 'profile' | 'company' = 'profile';

  companyName = '';
  industry = '';
  website = '';

  constructor() {
    if (this.isSocialSignup) {
      this.firstName = localStorage.getItem('ip-social-first-name') || '';
      this.lastName = localStorage.getItem('ip-social-last-name') || '';
    }
    // Pre-fill from a referral link's ?ref= param, stashed by
    // RegisterComponent a step earlier -- the field stays editable in case
    // the friend wants to change or clear it.
    this.referredByCode = localStorage.getItem('ip-referral-code') || '';
  }

  createAccount() {
    if (!this.firstName || !this.lastName || (!this.isSocialSignup && !this.password)) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const email = localStorage.getItem('verifyEmail') || '';
    const role = localStorage.getItem('ip-auth-role') || 'candidate';

    const profileTypeMap: Record<string, string> = {
      candidate: 'CANDIDATE',
      panel: 'PANELIST',
      corporates: 'CORPORATE',
    };

    const profileType = profileTypeMap[role] || 'CANDIDATE';

    if (this.isSocialSignup) {
      this.authService
        .createUserProfile({
          email,
          firstName: this.firstName,
          lastName: this.lastName,
          profileType,
          referredByCode: this.referredByCode.trim() || undefined,
        })
        .subscribe({
          next: () => {
            this.loading = false;
            localStorage.removeItem('ip-auth-provider');
            localStorage.removeItem('ip-social-first-name');
            localStorage.removeItem('ip-social-last-name');
            this._afterAccountCreated(role);
          },
          error: (err: any) => {
            this.loading = false;
            this.errorMessage =
              err?.error?.message ?? 'Could not save your profile. Please try again.';
            console.error('createUserProfile failed', err);
          },
        });
      return;
    }

    const body = {
      email,
      firstName: this.firstName,
      lastName: this.lastName,
      password: this.password,
      profileType,
      referredByCode: this.referredByCode.trim() || undefined,
    };
    this.authService.createUser(body).subscribe({
      next: (tokens: any) => {
        this.loading = false;
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);
        if (tokens.id_token) localStorage.setItem('id_token', tokens.id_token);

        this._afterAccountCreated(role);
      },
      error: (err: any) => {
        this.loading = false;
        this.errorMessage = err?.error?.message ?? 'Account creation failed. Please try again.';
        console.error('createUser failed', err);
      },
    });
  }

  /** Called once the account + UserProfile both exist and a valid access token
   *  is stored. Corporate signups must provide company details next (mandatory,
   *  see `saveCompany()`); everyone else goes straight in. */
  private _afterAccountCreated(role: string): void {
    // The code has already been sent to the backend by this point (it's
    // whatever was in the field when createAccount() ran) -- clear the
    // stashed link param so it doesn't leak into some unrelated later signup
    // sharing this browser (e.g. a corporate account created afterward).
    localStorage.removeItem('ip-referral-code');

    if (this.isCorporateSignup) {
      // Still on this page (just switching to the company-details step
      // below), so no redirect delay is needed, the toast and the new
      // step render at the same time.
      this.messageService.add({
        severity: 'success',
        summary: 'Account Created',
        detail: 'Now add your company details to finish setting up.',
      });
      this.step = 'company';
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Account Created',
      detail: "You're all set, taking you to your dashboard.",
      life: SUCCESS_REDIRECT_DELAY_MS + 2000,
    });
    setTimeout(() => this._redirectByRole(role), SUCCESS_REDIRECT_DELAY_MS);
  }

  saveCompany(): void {
    if (!this.companyName.trim()) {
      this.errorMessage = 'Company name is required.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authService
      .saveCompanyDetails({
        companyName: this.companyName.trim(),
        industry: this.industry.trim() || undefined,
        website: this.website.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Company Details Saved',
            detail: "You're all set, taking you to your dashboard.",
            life: SUCCESS_REDIRECT_DELAY_MS + 2000,
          });
          setTimeout(() => this._redirectByRole('corporates'), SUCCESS_REDIRECT_DELAY_MS);
        },
        error: (err: any) => {
          this.loading = false;
          this.errorMessage =
            err?.error?.message ?? 'Could not save your company details. Please try again.';
          console.error('saveCompanyDetails failed', err);
        },
      });
  }

  private _redirectByRole(role: string): void {
    if (role === 'panel') {
      this.router.navigate(['/app/panelist']);
    } else if (role === 'corporates') {
      this.router.navigate(['/app/employer']);
    } else {
      this.router.navigate(['/app/candidate']);
    }
  }
}
